/**
 * Stylet (S Pen) — types + appels API pour les strokes manuscrits
 * affichés en mode audience tablette.
 *
 * Format de stockage : points en pourcentage 0-1 de la page pour rester
 * résilients au niveau de zoom et au redimensionnement du canvas.
 */
import { api } from './api';

export type StrokeTool = 'pen' | 'highlighter';

/** Un point capturé : [x_pct, y_pct, pressure, t_ms_offset]. */
export type StrokePoint = [number, number, number, number];

export type StrokeData = {
  points: StrokePoint[];
};

export type Stroke = {
  id: string;
  pdf_file_id: string;
  page_number: number;
  stroke_data: StrokeData;
  color: string;
  width: number;
  tool: StrokeTool;
  created_by: string | null;
  created_at: string;
};

export type StrokeDraftInput = {
  page_number: number;
  stroke_data: StrokeData;
  color: string;
  width: number;
  tool: StrokeTool;
};

export const STROKE_PRESET_COLORS = [
  '#1a1a1a', // noir
  '#1d4ed8', // bleu marine
  '#dc2626', // rouge
  '#16a34a', // vert
  '#f59e0b', // ambre
  '#fde047', // jaune surligneur
] as const;

export const STROKE_PRESET_WIDTHS = [1, 2, 3, 5, 8] as const;

export async function listStrokes(pdfId: string, page?: number): Promise<Stroke[]> {
  const qs = page !== undefined ? `?page=${page}` : '';
  return api.get<Stroke[]>(`/api/v1/pdfs/${pdfId}/strokes${qs}`);
}

export async function bulkCreateStrokes(
  pdfId: string,
  strokes: StrokeDraftInput[]
): Promise<Stroke[]> {
  if (strokes.length === 0) return [];
  return api.post<Stroke[]>(`/api/v1/pdfs/${pdfId}/strokes`, { strokes });
}

export async function deleteStroke(strokeId: string): Promise<void> {
  await api.delete<void>(`/api/v1/strokes/${strokeId}`);
}

export async function resetPageStrokes(
  pdfId: string,
  pageNumber: number
): Promise<{ deleted: number }> {
  return api.delete<{ deleted: number }>(`/api/v1/pdfs/${pdfId}/pages/${pageNumber}/strokes`);
}
